import { ReportDataParams, ReportData } from 'app/dataTypes/reports/data'
import { ReportType, AnyReportType } from 'app/dataTypes/reports'
import { FinanceReportType } from 'app/dataTypes/reports/finances'

import type { Request } from '../../index'

import {
  convertResultCorrections,
  type ApiResultCorrections,
} from './_predefined/common/corrections'
import {
  convertResultSubsidiesGrouped,
  type ApiResultSubsidiesGrouped,
} from './_predefined/common/subsidies-grouped/data'
import {
  convertResultSubsidiesAct,
  type ApiResultSubsidiesAct,
} from './_predefined/common/subsidies-act/data'
import {
  convertResultOrdersByClasses,
  type ApiResultOrderByClasses,
} from './_predefined/common/orders-by-classes/data'
import {
  convertResultFinancesSummary,
  type ApiResultFinancesSummary,
} from './_predefined/common/finances-summary/financesSummary'
import {
  convertResultEmoneySales,
  type ApiResultEmoneySales,
} from './_predefined/finances/emoney-sales'
import {
  convertResultEncashments,
  type ApiResultEncashments,
} from './_predefined/finances/encashments'

import {
  ApiResultFinancesSummaryReplenishment,
  convertResultFinancesSummaryReplenishment,
} from './_predefined/common/finances-summary-replenisment'
import {
  ApiResultFinancesSummaryWriteOff,
  convertResultFinancesSummaryWriteOff,
} from './_predefined/common/finances-summary-write-off'

import {
  AverageCheckResultDto,
  convertResultAverageCheck,
} from './_predefined/common/average-check/data'

type ApiParams = {
  report_id: number
}

export const convertParams = <RT extends AnyReportType>(
  params: ReportDataParams<RT>,
): [ApiParams, null] => {
  const { reportID } = params
  return [{ report_id: parseInt(reportID, 10) }, null]
}

type ApiResultByReportType = {
  [FinanceReportType.EmoneySales]: ApiResultEmoneySales
  [FinanceReportType.Encashments]: ApiResultEncashments
  [ReportType.Corrections]: ApiResultCorrections
  [ReportType.SubsidiesGrouped]: ApiResultSubsidiesGrouped
  [ReportType.SubsidiesAct]: ApiResultSubsidiesAct
  [ReportType.OrdersByClasses]: ApiResultOrderByClasses
  [ReportType.FinancesSummary]: ApiResultFinancesSummary
  [ReportType.FinancesSummaryReplenishment]: ApiResultFinancesSummaryReplenishment
  [ReportType.FinancesSummaryWriteOff]: ApiResultFinancesSummaryWriteOff
}

type ApiResult<RT extends AnyReportType> = RT extends keyof ApiResultByReportType
  ? ApiResultByReportType[RT]
  : never

export const convertResult = <RT extends AnyReportType>(
  res: ApiResult<RT>,
  params: ReportDataParams<RT>,
): ReportData<RT> => {
  const { reportType } = params
  if (reportType === ReportType.Corrections) {
    return convertResultCorrections(res as ApiResultCorrections) as ReportData<RT>
  }
  if (reportType === ReportType.SubsidiesGrouped) {
    return convertResultSubsidiesGrouped(res as ApiResultSubsidiesGrouped) as ReportData<RT>
  }
  if (reportType === ReportType.SubsidiesAct) {
    return convertResultSubsidiesAct(res as ApiResultSubsidiesAct) as ReportData<RT>
  }
  if (reportType === ReportType.OrdersByClasses) {
    return convertResultOrdersByClasses(res as ApiResultOrderByClasses) as ReportData<RT>
  }
  if (reportType === FinanceReportType.EmoneySales) {
    return convertResultEmoneySales(res as ApiResultEmoneySales) as ReportData<RT>
  }
  if (reportType === FinanceReportType.Encashments) {
    return convertResultEncashments(res as ApiResultEncashments) as ReportData<RT>
  }
  if (reportType === ReportType.FinancesSummary) {
    return convertResultFinancesSummary(res as ApiResultFinancesSummary) as ReportData<RT>
  }
  if (reportType === ReportType.FinancesSummaryReplenishment) {
    return convertResultFinancesSummaryReplenishment(
      res as ApiResultFinancesSummaryReplenishment,
    ) as ReportData<RT>
  }
  if (reportType === ReportType.FinancesSummaryWriteOff) {
    return convertResultFinancesSummaryWriteOff(
      res as ApiResultFinancesSummaryWriteOff,
    ) as ReportData<RT>
  }
  if (reportType === ReportType.AverageCheck) {
    return convertResultAverageCheck(res as AverageCheckResultDto) as ReportData<RT>
  }
  // if (reportType === ReportType.OtherReport) return convertOther() as ReportData<RT>

  return { reason: 'convertResult not handled this report type' } as never
}

export const GET_REPORTS_DATA: Request<
  ReportDataParams<ReportType>,
  ReportData<ReportType>,
  void
> = ['reports/data', 'get', ['data', 'data']]
